import { useCallback, useEffect, useRef, useState } from "react"
import * as filterEnum from "enums/filterEnum"
import * as util from "lib/util"
import { AsyncDropdown } from "components"
import { FormHelperText, TextFieldProps, useTheme, alpha, Box } from "@mui/material"
import { motion } from "framer-motion"
import * as hooks from "lib/hooks"

export interface DropdownFilterProps<
	FieldType extends string,
	I,
	O = I,
	V extends O[] | O | null = O | null,
> {
	filterType: filterEnum.FilterEnum<FieldType, I, O>
	getValue: (props: { field: FieldType; data: O[] }) => V
	onChange: (props: { strValue: () => string; field: FieldType; value: V }) => void
	keyFunc: (item: O) => string
	labelFunc?: (item: O) => string
	onDataLoaded?: (data: O[]) => void
	label?: string
	errorMsg?: string
	multiple?: boolean
	disabled?: boolean
	required?: boolean
	autoTrigger?: boolean
	textFieldProps?: TextFieldProps
	hideLabel?: bool
	staticOptions?: O[], // provide local options instead of fetching
	getOptionDisabled?: (option: O) => boolean
	getOptionClassName?: (option: O) => string | undefined
	groupBy?: (option: O) => string
}

export function DropdownFilter<
	FieldType extends string,
	I,
	O extends object,
	V extends O[] | O | null,
>({
	filterType,
	getValue,
	onChange,
	label: labelOverride,
	keyFunc,
	labelFunc,
	errorMsg,
	multiple: multipleOverride,
	disabled,
	required,
	autoTrigger,
	onDataLoaded,
	textFieldProps,
	hideLabel,
	staticOptions,
	getOptionDisabled,
	getOptionClassName,
	groupBy,
}: DropdownFilterProps<FieldType, I, O, V>): React.JSX.Element | nil {
	const theme = useTheme()
	const api = hooks.UseMccApi()
	return filterType
		.IfLet()
		.Dropdown((dropdownProps) => {
			const { field, label, multiple, getter } = dropdownProps
			const [items, setItems] = useState<O[]>(staticOptions || [])
			const [loading, setLoading] = useState(false)
			const [error, setError] = useState<Error | null>(null)
			const initialDataProcessedRef = useRef(false)
			const fetchInitiatedRef = useRef(false)

			// Inline fetch logic in useEffect to avoid dependency cascade
			// eslint-disable-next-line react-hooks/exhaustive-deps
			useEffect(() => {
				if (staticOptions) return // skip auto fetch when static options provided
				if (autoTrigger && !fetchInitiatedRef.current) {
					fetchInitiatedRef.current = true
					
					// Inline the fetch logic
					const doFetch = async () => {
						setLoading(true)
						setError(null)
						try {
							const data = await getter({ api: () => api })
							setItems(data)
						} catch (err) {
							setError(err as Error)
							setItems([])
						} finally {
							setLoading(false)
						}
					}
					
					doFetch()
				}
			}, [autoTrigger, staticOptions]) // Only depend on these to prevent re-fetching
			
			// Update items when staticOptions changes
			useEffect(() => {
				if (staticOptions) {
					setItems(staticOptions)
					if (onDataLoaded && !initialDataProcessedRef.current && staticOptions.length > 0) {
						initialDataProcessedRef.current = true
						onDataLoaded(staticOptions)
					}
				}
			}, [staticOptions, onDataLoaded])

			useEffect(() => {
				if (items.length > 0 && !initialDataProcessedRef.current && onDataLoaded) {
					initialDataProcessedRef.current = true
					onDataLoaded(items)
				}
			}, [items, onDataLoaded])

			// Memoized fetch function for manual triggering (e.g., from AsyncDropdown refresh)
			const fetchData = useCallback(async () => {
				if (staticOptions) {
					setItems(staticOptions)
					return
				}
				setLoading(true)
				setError(null)
				try {
					const data = await getter({ api: () => api })
					setItems(data)
				} catch (err) {
					setError(err as Error)
					setItems([])
				} finally {
					setLoading(false)
				}
			}, [getter, staticOptions, api])

			const currentValue = getValue({ field, data: items })

			const handleChange = useCallback(
				(newValue: V) => {
					const strValue = (): string => {
						if (Array.isArray(newValue)) {
							return newValue.Map(keyFunc).Join(util.SearchQueryDelimiter)
						} else if (newValue) {
							return keyFunc(newValue as O)
						}
						return ""
					}

					onChange({ strValue, field, value: newValue })
				},
				[field, keyFunc, onChange]
			)

			const dropdownVariants = {
				hidden: { opacity: 0, y: 5 },
				visible: {
					opacity: 1,
					y: 0,
					transition: {
						type: "spring" as const,
						stiffness: 300,
						damping: 24,
						duration: 0.25,
					},
				},
			}

			return (
				<motion.div
					initial="hidden"
					animate="visible"
					variants={dropdownVariants}
					style={{ width: "100%" }}
				>
					<AsyncDropdown
						data-test-selector={`dropdown-filter-${label}`}
						data={items}
						textFieldProps={{
							...textFieldProps,
							placeholder: textFieldProps?.placeholder || `Select ${label}`,
						}}
						disabled={disabled}
						getter={fetchData}
						required={required}
						hideLabel={hideLabel}
						loading={loading}
						label={labelOverride || label}
						size="small"
						multiple={multipleOverride ?? !!multiple}
						value={currentValue}
						onChange={handleChange as any}
						getOptionLabel={labelFunc || keyFunc}
						isOptionEqualToValue={(option, value) => keyFunc(option) === keyFunc(value)}
						getOptionDisabled={getOptionDisabled}
						getOptionClassName={getOptionClassName}
						groupBy={groupBy}
					/>
					{errorMsg && (
						<FormHelperText
							error
							sx={{
								fontWeight: 500,
								ml: 1.5,
								fontSize: "0.75rem",
								mt: 0.5,
								opacity: 0.9,
								color: theme.palette.error.main,
							}}
						>
							{errorMsg}
						</FormHelperText>
					)}
					{error && (
						<FormHelperText
							error
							sx={{
								fontWeight: 500,
								ml: 1.5,
								fontSize: "0.75rem",
								mt: 0.5,
								opacity: 0.9,
								color: theme.palette.error.main,
							}}
						>
							{util.GetMsgFromError(error)}
						</FormHelperText>
					)}
				</motion.div>
			)
		})
		.UnwrapOrNil()
}
